/**
 * Landing-page block model.
 *
 * The SAME object the drag-and-drop editor manipulates is what the edge Worker
 * renders. There is no translation step between "what the operator arranged"
 * and "what the visitor sees" — a translation layer is where the two drift
 * apart and the preview stops matching production.
 *
 * Runtime-agnostic: this runs in the browser (editor), in Node (API preview)
 * and on Cloudflare Workers (public render). No Node built-ins.
 */

import { ACOES_PADRAO, CAMPOS_PADRAO } from './formulario.js'
import type { Pixel } from './medicao.js'

export const TIPOS_BLOCO = [
  'hero',
  'texto',
  'imagem',
  'colunas',
  'beneficios',
  'prazo',
  'formulario',
  'prova',
  'faq',
  'cta',
  'contato',
  'rodape',
] as const

export type TipoBloco = (typeof TIPOS_BLOCO)[number]

/**
 * Presentation choices that apply to any block.
 *
 * Kept apart from `props` because they are the same everywhere: a background or
 * a column count means the same thing on a FAQ as on a benefits list, and
 * duplicating them per block type is how one of them ends up unimplemented.
 *
 * `colunas` is a *maximum*, never a fixed grid: the rendered CSS collapses to
 * one column on a narrow screen no matter what is set here. A landing page that
 * keeps three columns on a phone is not a design decision, it is a bug — and
 * roughly two thirds of the people opening these pages are on a phone.
 */
export interface LayoutBloco {
  colunas?: 1 | 2 | 3
  fundo?: 'claro' | 'suave' | 'escuro' | 'marca'
  alinhamento?: 'esquerda' | 'centro'
  espacamento?: 'compacto' | 'normal' | 'amplo'
}

export interface Bloco {
  id: string
  tipo: TipoBloco
  /** Per-block properties; shape depends on `tipo`. */
  props: Record<string, unknown>
  /** Presentation, the same vocabulary for every block type. */
  layout?: LayoutBloco
  /** Hidden blocks stay in the tree so the operator can toggle without losing content. */
  oculto?: boolean
}

/**
 * An image served from our own origin.
 *
 * `largura`/`altura` are the intrinsic dimensions and they are not optional:
 * without them the browser cannot reserve the space, and the whole page jumps
 * when the photo arrives. That jump is the single most common reason a visitor
 * taps the wrong thing on a phone.
 */
export interface ImagemBloco {
  /** Caminho na nossa API: /publico/midia/<id> */
  url: string
  alt: string
  largura: number
  altura: number
}

export interface TemaPagina {
  corPrimaria?: string
  corAcao?: string
  corFundo?: string
  corTexto?: string
  fonte?: string
}

export interface PaginaLanding {
  slug: string
  titulo: string
  blocos: Bloco[]
  tema: TemaPagina
  seo: { titulo?: string; descricao?: string; imagem?: string }
  pixels: Pixel[]
}

export const TEMA_PADRAO: Required<TemaPagina> = {
  corPrimaria: '#0c64b0',
  corAcao: '#ff6b35',
  corFundo: '#ffffff',
  corTexto: '#0f172a',
  fonte: "Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
}

/** Human labels for the editor palette. */
export const ROTULOS_BLOCO: Record<TipoBloco, { nome: string; descricao: string }> = {
  hero: { nome: 'Destaque', descricao: 'Título principal, subtítulo e chamada' },
  texto: { nome: 'Texto', descricao: 'Parágrafo livre com título opcional' },
  imagem: { nome: 'Imagem', descricao: 'Foto de largura total, com legenda opcional' },
  colunas: { nome: 'Colunas', descricao: 'Duas ou três colunas com conteúdo próprio' },
  beneficios: { nome: 'Benefícios', descricao: 'Lista de itens com ícone' },
  prazo: { nome: 'Prazo', descricao: 'Aviso de prazo legal com data' },
  formulario: { nome: 'Formulário', descricao: 'Captura de lead — nome, e-mail, CNPJ' },
  prova: { nome: 'Prova social', descricao: 'Números ou depoimentos' },
  faq: { nome: 'Perguntas frequentes', descricao: 'Lista de pergunta e resposta' },
  cta: { nome: 'Chamada final', descricao: 'Botão de conversão destacado' },
  contato: { nome: 'Canais de contato', descricao: 'WhatsApp, telefone e e-mail com link direto' },
  rodape: { nome: 'Rodapé', descricao: 'Identificação institucional e links legais' },
}

/** Sensible presentation per block type, so a dropped block already looks right. */
const LAYOUT_PADRAO: Record<TipoBloco, LayoutBloco> = {
  hero: { fundo: 'marca', alinhamento: 'esquerda', espacamento: 'amplo' },
  texto: { fundo: 'claro', alinhamento: 'esquerda', espacamento: 'normal' },
  imagem: { fundo: 'claro', alinhamento: 'centro', espacamento: 'compacto' },
  colunas: { colunas: 3, fundo: 'claro', alinhamento: 'esquerda', espacamento: 'normal' },
  beneficios: { colunas: 3, fundo: 'suave', alinhamento: 'esquerda', espacamento: 'normal' },
  prazo: { fundo: 'claro', alinhamento: 'esquerda', espacamento: 'normal' },
  formulario: { fundo: 'suave', alinhamento: 'esquerda', espacamento: 'amplo' },
  prova: { colunas: 3, fundo: 'claro', alinhamento: 'centro', espacamento: 'normal' },
  faq: { fundo: 'claro', alinhamento: 'esquerda', espacamento: 'normal' },
  cta: { fundo: 'marca', alinhamento: 'centro', espacamento: 'amplo' },
  contato: { fundo: 'suave', alinhamento: 'esquerda', espacamento: 'normal' },
  rodape: { fundo: 'escuro', alinhamento: 'esquerda', espacamento: 'compacto' },
}

let contador = 0

/** Stable-ish id for a new block. Not a UUID — it only needs to be unique in the page. */
export function novoIdBloco(): string {
  contador += 1
  return `b${Date.now().toString(36)}${contador.toString(36)}`
}

/** A new block with sensible defaults, so dropping one never yields an empty box. */
export function blocoPadrao(tipo: TipoBloco): Bloco {
  const props: Record<TipoBloco, Record<string, unknown>> = {
    hero: {
      titulo: 'Publique a relação de concluintes no Diário Oficial',
      subtitulo:
        'A Resolução CEE nº 44/2020 exige a publicação da relação nominal dos concluintes do Ensino Médio e da Educação Profissional Técnica.',
      textoBotao: 'Solicitar publicação',
      selo: 'Empresa Gráfica da Bahia · Governo do Estado',
    },
    texto: { titulo: 'Sobre a obrigação', corpo: 'Descreva aqui o contexto da publicação.' },
    imagem: { legenda: '' },
    colunas: {
      titulo: 'O que a EGBA entrega',
      itens: [
        { titulo: 'Diagramação', texto: 'A matéria é preparada no padrão do Diário Oficial.' },
        { titulo: 'Publicação', texto: 'Sai na edição combinada, com data e página.' },
        { titulo: 'Comprovante', texto: 'Você recebe o PDF da página publicada.' },
      ],
    },
    beneficios: {
      titulo: 'Como funciona',
      itens: [
        { titulo: 'Envie a lista', texto: 'Planilha ou PDF com nome, curso e ano de conclusão.' },
        { titulo: 'Nós diagramamos', texto: 'A matéria é preparada no padrão do Diário Oficial.' },
        { titulo: 'Receba o comprovante', texto: 'Edição, data e página, com comprovante em PDF.' },
      ],
    },
    prazo: {
      titulo: 'Prazo legal',
      texto:
        'A publicação deve ocorrer logo após o término dos cursos. Sem ela, os diplomas ficam sem a publicidade que a norma exige.',
      referencia: 'Resolução CEE nº 44/2020, Art. 4º',
    },
    formulario: {
      titulo: 'Solicite sua publicação',
      subtitulo: 'Retornamos com o orçamento e o prazo.',
      campos: CAMPOS_PADRAO,
      acoes: ACOES_PADRAO,
    },
    // O que for digitado aqui é publicado como FATO, num site do Governo do
    // Estado. Os padrões abaixo são afirmações que a própria EGBA sustenta —
    // o processo que ela executa e o alcance legal do DOE, que é a gazeta
    // oficial de toda a Bahia (417 municípios). Ao trocar por número de
    // desempenho, o operador precisa ter a fonte.
    prova: {
      titulo: 'O Diário Oficial do Estado da Bahia',
      itens: [
        { valor: '417', rotulo: 'municípios no alcance do DOE-BA' },
        { valor: '3', rotulo: 'passos: enviar, aprovar, publicar' },
        { valor: 'PDF', rotulo: 'comprovante da página publicada' },
      ],
    },
    faq: {
      titulo: 'Perguntas frequentes',
      itens: [
        {
          pergunta: 'Quem precisa publicar?',
          resposta:
            'Instituições de ensino, públicas e privadas, que concluem turmas de Ensino Médio ou de Educação Profissional Técnica de Nível Médio.',
        },
        {
          pergunta: 'Quem paga a publicação?',
          resposta:
            'Nas instituições privadas, o ônus é da própria escola, conforme o Art. 4º, § 1º da Resolução CEE nº 44/2020.',
        },
      ],
    },
    cta: {
      titulo: 'Regularize a publicação da sua turma',
      textoBotao: 'Falar com a EGBA',
    },
    // Os canais padrão são os REAIS da EGBA (config `contato.egba`). WhatsApp
    // nasce vazio de propósito: número inventado aqui viraria um botão morto
    // numa página do Governo — o operador preenche quando o canal existir.
    contato: {
      titulo: 'Prefere falar com alguém?',
      subtitulo: 'A equipe de publicações da EGBA atende nestes canais.',
      whatsapp: '',
      mensagemWhatsapp: 'Olá! Preciso publicar a relação de concluintes no Diário Oficial da Bahia.',
      telefones: ['(71) 3116-2850', '(71) 3116-2133', '(71) 3116-2863'],
      email: 'publicacoes@egba.ba.gov.br',
      assuntoEmail: 'Publicação de relação de concluintes — Diário Oficial',
    },
    rodape: {
      texto: 'Empresa Gráfica da Bahia — EGBA · Casa Civil · Governo do Estado da Bahia',
      contato: 'publicacoes@egba.ba.gov.br · (71) 3116-2850',
    },
  }

  return { id: novoIdBloco(), tipo, props: props[tipo], layout: LAYOUT_PADRAO[tipo] }
}

/** Page skeleton for a brand-new landing page. */
export function paginaPadrao(slug: string, titulo: string): PaginaLanding {
  return {
    slug,
    titulo,
    blocos: [
      blocoPadrao('hero'),
      blocoPadrao('prazo'),
      blocoPadrao('beneficios'),
      blocoPadrao('prova'),
      blocoPadrao('formulario'),
      blocoPadrao('faq'),
      blocoPadrao('rodape'),
    ],
    tema: { ...TEMA_PADRAO },
    seo: { titulo, descricao: '' },
    pixels: [],
  }
}

export interface ProblemaPagina {
  bloco?: string
  mensagem: string
}

/**
 * Checks a page before publishing.
 *
 * A landing page without a form captures nothing, and one without a footer has
 * no legal identification — both are mistakes worth catching before the page is
 * live and consuming ad spend.
 */
export function validarPagina(pagina: PaginaLanding): ProblemaPagina[] {
  const problemas: ProblemaPagina[] = []
  const visiveis = pagina.blocos.filter((b) => !b.oculto)

  if (visiveis.length === 0) problemas.push({ mensagem: 'A página não tem nenhum bloco visível.' })

  if (!visiveis.some((b) => b.tipo === 'formulario')) {
    problemas.push({
      mensagem: 'Sem bloco de formulário a página não captura lead nenhum.',
    })
  }

  if (!visiveis.some((b) => b.tipo === 'rodape')) {
    problemas.push({
      mensagem: 'Sem rodapé a página fica sem identificação institucional e sem link legal.',
    })
  }

  if (!pagina.seo.descricao?.trim()) {
    problemas.push({ mensagem: 'Descrição de SEO vazia — prejudica o anúncio e o compartilhamento.' })
  }

  for (const b of visiveis) {
    if (b.tipo === 'hero' && !String(b.props.titulo ?? '').trim()) {
      problemas.push({ bloco: b.id, mensagem: 'O bloco de destaque está sem título.' })
    }
    if (b.tipo === 'contato') {
      const tels = Array.isArray(b.props.telefones) ? (b.props.telefones as unknown[]) : []
      const temCanal =
        String(b.props.whatsapp ?? '').trim() !== '' ||
        String(b.props.email ?? '').trim() !== '' ||
        tels.some((t) => String(t ?? '').trim() !== '')
      if (!temCanal) {
        problemas.push({
          bloco: b.id,
          mensagem: 'O bloco de canais de contato está sem nenhum canal preenchido.',
        })
      }
    }
    if (b.tipo === 'formulario') {
      // O consentimento passou a viver em `props.acoes`. A leitura aceita as
      // duas posições porque as páginas criadas antes da mudança continuam no
      // banco — e uma página válida não pode virar inválida por causa de uma
      // refatoração nossa.
      const acoes = (b.props.acoes ?? {}) as { textoConsentimento?: unknown }
      const texto = String(acoes.textoConsentimento ?? b.props.textoConsentimento ?? '').trim()
      if (!texto) {
        problemas.push({
          bloco: b.id,
          mensagem: 'O formulário precisa do texto de consentimento (LGPD).',
        })
      }

      const campos = Array.isArray(b.props.campos) ? (b.props.campos as Array<{ nome?: unknown }>) : null
      if (campos && !campos.some((c) => c.nome === 'email')) {
        problemas.push({
          bloco: b.id,
          mensagem: 'Sem campo de e-mail o formulário não gera contato aproveitável.',
        })
      }
    }
  }

  return problemas
}
